import { Attribute } from '../Model/Attribute';
import { ProductInterface } from '../Model/Product';
import { AttributeUtility } from '../Service/AttributeUtility';
import { ObjectUtility } from '../Service/ObjectUtility';
import { FilterInterface, FilterType } from './FilterInterface';

interface ProductArrayAccess<T = any> {
    [key: string]: T
}

interface AttributeOrValue {
    displayString: string
    numericValue?: number;
}

export abstract class AbstractFilter<T> implements FilterInterface<T> {
    public readonly keyPath: string;
    public readonly type: FilterType;
    protected _value: T;
    protected _isActive: boolean;

    constructor(keyPath: string, expectedValue: T, type: FilterType, isActive: boolean) {
        this.type = type;
        this.keyPath = keyPath;
        this._value = expectedValue;
        this._isActive = isActive;
    }

    get isActive(): boolean {
        return this._isActive;
    }

    get value(): T {
        return this._value;
    }

    public abstract matches(product: ProductInterface): boolean;

    public cleared(): FilterInterface<T> {
        const clone = this.clone() as this;
        clone._isActive = false;
        clone._value = this.getClearValue();

        return clone;
    }

    public withValue(value: T): FilterInterface<T> {
        const clone = this.clone() as this;
        clone._value = value;

        return clone;
    }

    public withActive(isActive: boolean): FilterInterface<T> {
        const clone: AbstractFilter<T> = this.clone() as this;
        clone._isActive = isActive;

        return clone;
    }

    protected abstract clone(): FilterInterface<T>;

    protected getClearValue(): T | any | undefined {
        const value = this.value;
        const oldType = typeof value;
        if (oldType === 'string') {
            return '';
        } else if (oldType === 'number') {
            return 0;
        } else if (Array.isArray(value)) {
            return [];
        } else if (oldType === 'object') {
            return {};
        } else {
            return undefined;
        }
    }

    protected productMatches<V = T>(product: ProductInterface, keyPath: string, value: V): boolean {
        const attribute = this.valueForKey(product, keyPath);
        const isMatch = this.matchAttribute(attribute, value);
        console.debug(
            `[Filter] Compare property "${keyPath}" of product ${product.name} (#${product.guid}): ${isMatch ? 'match'
                : 'no match'}`,
            value,
            attribute,
            product,
        );

        return isMatch;
    }

    protected matchAttribute<V = T>(attribute: AttributeOrValue | undefined, testValue: V): boolean {
        // If testValue is `undefined` the attribute must be `undefined` too
        if (typeof testValue === 'undefined') {
            return typeof attribute === 'undefined';
        }
        if (typeof attribute === 'undefined') {
            return false;
        }

        if (attribute as any === testValue) {
            return true;
        }
        if (attribute.displayString === testValue as any) {
            return true;
        }

        const valueAsString = '' + testValue;
        if (attribute.displayString === valueAsString) {
            return true;
        }
        if ('' + attribute.numericValue === valueAsString) {
            return true;
        }

        if (attribute.numericValue === testValue as any) {
            return true;
        }

        if (attribute instanceof Attribute && attribute.isMultiValue && attribute.localeValueSet) {
            return 'undefined' !== typeof attribute.localeValueSet.values.find(
                av => this.matchAttribute(av, testValue));
        }

        return false;
    }

    protected resolveAttributes(product: ProductInterface, keyPath: string): (Attribute | undefined)[] {
        if (keyPath.substr(0, 7) === '@child.') {
            return AttributeUtility.attributesForKey(product.children, keyPath.substr(7));
        } else {
            return [this.valueForKey(product, keyPath)];
        }
    }

    protected valueForKey(product: ProductInterface | ProductArrayAccess, keyPath: string): Attribute | undefined {
        if (keyPath.substr(0, 7) === '@child.') {
            throw new Error('@child can not be used with `valueForKey`');
        }

        return ObjectUtility.valueForKeyPath(product, keyPath);
    }
}
